/* ***************************************************************** */
/* TODO: eslint pre-existing exceptions auto generated on 2022-09-08 */
/*       Do not add to these rule exceptions. Only remove as the     */
/*       violations are cleaned from our code.                       */
/*                                                                   */

/*       eslint-disable no-nested-ternary                            */
/* ***************************************************************** */
/* TODO: fix this 👇 */

import React, { useState } from 'react';
import { Box, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import { GridActionsCellItem } from '@mui/x-data-grid-pro';
import FieldDetails from './FieldDetails';
import { getRounded } from '../../../lib/numbers';
import { normalizeStatus } from '../../../lib/statuses';
import {
  downloadNrxPdfs,
  downloadNrxShapefile,
  setNrxAdjustmentFactor,
  setNrxGroupAdjustment,
} from '../../../lib/api';
import { generateCsvDownload } from '../../../lib/csv';
import { getFormattedDate } from '../../../lib/dates';
import { usePermissions } from '../../../hooks/use-permissions';
import { useMapRefresh } from '../../../components/google/maps/use-map-refresh';
import { DownloadButton } from '../../../components/DownloadButton';
import { Table } from '../../../components/table/Table';
import { useGrowers } from '../../../hooks/use-growers';
import { SendToDeereButton } from '../../../components/deere/SendToDeereButton';

export default function FieldNrx(props) {
  const { fields, selectedFields, reloadFields, isLoading } = props;
  const { selectedId: growerId } = useGrowers();
  const [showStats, setShowStats] = useState(false);
  const [statsField, setStatsField] = useState(null);
  const permissions = usePermissions();
  const { refresh } = useMapRefresh();

  const hideStats = () => {
    setShowStats(false);
    setStatsField(null);
  };

  const downloadShapefiles = () =>
    downloadNrxShapefile(growerId, getDownloadableAaids());

  const downloadPdf = () => downloadNrxPdfs(growerId, getDownloadableAaids());

  const onAdjustClick = (e, row) => {
    e.stopPropagation();
    e.preventDefault();

    setStatsField(row);
    setShowStats(true);
  };

  const downloadCsv = () => {
    const filename = `nrx-export-${growerId}.csv`;

    let csvArr = fields;
    if (selectedFields && selectedFields.length > 0) {
      const selectedAAIDs = selectedFields
        ? selectedFields.map((f) => f.aaManagedId.toUpperCase())
        : [];
      csvArr = fields.filter((f) =>
        selectedAAIDs.includes(f.aaManagedId.toUpperCase()),
      );
    }

    const csvData = csvArr.map((f) => ({
      field_name: f.fieldName,
      farm_name: f.farmName,
      field_id: f.fieldId,
      acres: getRounded(f.acres, 2),
      min_nitrogen_rate: f.minNitrogenRate,
      max_nitrogen_rate: f.maxNitrogenRate,
      avg_nitrogen_rate: getRounded(f.avgNitrogenRate, 2),
      est_total_nitrogen: getRounded(f.estTotalNitrogen, 0),
      status: f.rxStatus ? normalizeStatus(f.rxStatus, f.hasNrx) : 'N/A',
      created:
        normalizeStatus(f.rxStatus, f.hasNrx) === 'Complete'
          ? getFormattedDate(f.createdDate)
          : '',
      corn_after_corn:
        normalizeStatus(f.rxStatus, f.hasNrx) !== 'Complete' ||
        f.cornAfterCorn === null
          ? 'N/A'
          : f.cornAfterCorn
            ? 'Yes'
            : 'No',
      soil_sample_date: getFormattedDate(f.soilSampleDate),
      aaid: f.aaManagedId,
    }));

    generateCsvDownload(csvData, filename);
  };

  const updateAdjustmentFactor = (aaId, adjustmentFactor, groupAdjustments) => {
    // Only need to store the group adjustment offset from the overall adjustment factor, so
    // map those values accordingly.
    const groupAdjustmentOffsets = groupAdjustments.map((groupAdjustment) => ({
      ...groupAdjustment,
      adjustmentFactor:
        (groupAdjustment.adjustmentFactor || 0) - (adjustmentFactor || 0),
    }));
    Promise.all([
      setNrxAdjustmentFactor(aaId, adjustmentFactor),
      setNrxGroupAdjustment(aaId, groupAdjustmentOffsets),
    ]).then(() => {
      refresh();
      reloadFields(true);
      setStatsField(null);
    });
  };

  function isFieldDownloadable(field) {
    return fields.find((f) => f.aaManagedId === field.aaManagedId)
      ?.estTotalNitrogen;
  }

  function getDownloadableAaids() {
    return selectedFields.filter(isFieldDownloadable).map((f) => f.aaManagedId);
  }

  function getDownloadablePrescriptions() {
    const aaids = getDownloadableAaids();
    return fields?.filter((f) => aaids.includes(f.aaManagedId));
  }

  return (
    <Box sx={{ height: '100%' }}>
      {buildGrid()}
      {showStats && statsField && (
        <FieldDetails
          field={statsField}
          savedAdjustmentFactor={statsField.adjustmentFactor}
          handleClose={hideStats}
          handleUpdateAdjustmentFactor={updateAdjustmentFactor}
        />
      )}
    </Box>
  );

  function buildActions() {
    return [
      permissions.includes('DOWNLOAD_RX') && (
        <DownloadButton
          keep-open="true"
          disabled={!getDownloadableAaids().length}
          download={downloadShapefiles}
          data-testid="download-btn"
        >
          Download
        </DownloadButton>
      ),
      <DownloadButton key="csv" keep-open="true" download={downloadCsv}>
        Export CSV
      </DownloadButton>,
      <DownloadButton
        keep-open="true"
        data-testid="print-pdf-btn"
        disabled={!getDownloadableAaids().length}
        download={downloadPdf}
      >
        Print to PDF
      </DownloadButton>,
      <SendToDeereButton
        type="n"
        prescriptions={getDownloadablePrescriptions()}
      />,
    ].filter(Boolean);
  }

  function getCornAfterCorn(row) {
    if (
      normalizeStatus(row.rxStatus, row.hasNrx) !== 'Complete' ||
      row.cornAfterCorn === null
    ) {
      return 'N/A';
    }

    if (row.cornAfterCorn) return 'Yes';
    return 'No';
  }

  function buildGrid() {
    return (
      <Table
        loading={isLoading}
        fields={fields || []}
        columns={buildColumns()}
        actions={buildActions()}
      />
    );
  }

  function buildColumns() {
    return [
      {
        field: 'plannedCrop',
        headerName: 'Planned Crop',
        flex: 0.5,
        minWidth: 75,
        align: 'center',
      },
      {
        field: 'createdDate',
        headerName: 'Created',
        flex: 1,
        type: 'date',
        minWidth: 90,
        valueFormatter: ({ value }) => getFormattedDate(value),
      },
      {
        field: 'cornAfterCorn',
        headerName: 'Corn after Corn',
        flex: 0.5,
        minWidth: 75,
        renderCell: ({ row }) => (
          <Box sx={{ display: 'flex', justifyContent: 'right' }}>
            {getCornAfterCorn(row) || ''}
          </Box>
        ),
      },
      {
        field: 'soilSampleDate',
        headerName: 'Soil Date',
        flex: 1,
        type: 'date',
        minWidth: 90,
        valueFormatter: ({ value }) =>
          value ? new Date(value).toLocaleDateString() : null,
      },
      {
        field: 'minNitrogenRate',
        headerName: 'Min lbs/ac',
        flex: 1,
        type: 'number',
        minWidth: 100,
        valueFormatter: ({ value }) => getRounded(value, 0),
      },
      {
        field: 'maxNitrogenRate',
        headerName: 'Max lbs/ac',
        flex: 1,
        type: 'number',
        minWidth: 100,
        valueFormatter: ({ value }) => getRounded(value, 0),
      },
      {
        field: 'avgNitrogenRate',
        headerName: 'Avg lbs/ac',
        flex: 1,
        type: 'number',
        minWidth: 100,
        valueFormatter: ({ value }) => getRounded(value, 2),
      },
      {
        field: 'estTotalNitrogen',
        headerName: 'Total lbs',
        flex: 1,
        type: 'number',
        minWidth: 100,
        valueFormatter: ({ value }) => getRounded(value, 0, true),
      },
      {
        field: 'rxStatus',
        headerName: 'Rx Status',
        flex: 1,
        renderCell: ({ row }) => (
          <>
            {row.rxStatus || 'N/A'}
            {row.rxStatus === 'Failed' && (
              <Tooltip title={row.rxStatus}>
                <InfoIcon />
              </Tooltip>
            )}
          </>
        ),
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Adjust',
        getActions: ({ row }) => [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Adjust"
            onClick={(e) => onAdjustClick(e, row)}
            disabled={!row.rxStatus?.toLowerCase().includes('complete')}
            data-testid={`adjust-${row.aaManagedId}`}
          />,
        ],
      },
    ];
  }
}
